
























































import Vue from "vue";
import { mapGetters } from "vuex";
import {
  buildTwoFactorRoute,
  EMPTY_TWO_FACTOR_CHALLENGE,
  MFA_SESSION_EXPIRED_MESSAGE,
  resendCodeLabel,
  resendFailureCooldownSeconds,
  resolveActiveChallenge,
  resolveVerifyToken,
  TWO_FACTOR_RESEND_COOLDOWN_SECONDS,
  TWO_FACTOR_RESEND_SUCCESS_MESSAGE,
  TwoFactorChallenge,
  TwoFactorResendResult,
} from "@/utils/twoFactor/twoFactor";

const CODE_LENGTH = 6;

export default Vue.extend({
  name: "TwoFactorLoginV2",
  data() {
    return {
      CODE_LENGTH,
      digits: Array<string>(CODE_LENGTH).fill(""),
      isSubmitting: false,
      isResending: false,
      resendFeedbackMessage: "",
      resendCooldownSeconds: 0,
      resendCooldownTimer: null as ReturnType<typeof setInterval> | null,
    };
  },
  mounted() {
    this.$nextTick(() => this.focusInput(0));
    if (this.challenge.initialError) {
      this.$store.dispatch("setWarning", this.challenge.initialError);
    }
    if (!this.challenge.skipInitialCooldown) {
      this.startResendCooldown(TWO_FACTOR_RESEND_COOLDOWN_SECONDS);
    }
  },
  beforeDestroy() {
    this.clearResendCooldown();
  },
  computed: {
    ...mapGetters(["twoFactorChallenge"]),
    challenge(): TwoFactorChallenge {
      return resolveActiveChallenge(
        this.$route.query.oauth,
        this.twoFactorChallenge || EMPTY_TWO_FACTOR_CHALLENGE,
      );
    },
    verifyToken(): string {
      return resolveVerifyToken(this.$route.query.oauth, this.challenge);
    },
    code(): string {
      return this.digits.join("");
    },
    phoneLast4(): string {
      const last4 = this.$route.query.phone_last4;
      if (typeof last4 === "string" && last4) {
        return last4;
      }
      return this.challenge.phoneLast4 || "";
    },
    subtitle(): string {
      const base =
        "We have sent a verification code to the mobile number you have provided";
      return this.phoneLast4
        ? `${base} (number ending ${this.phoneLast4}).`
        : `${base}.`;
    },
    canResend(): boolean {
      return (
        !this.isResending &&
        !this.isSubmitting &&
        this.resendCooldownSeconds === 0
      );
    },
    resendLabel(): string {
      return resendCodeLabel(this.resendCooldownSeconds, this.isResending);
    },
  },
  methods: {
    focusInput(index: number): void {
      const inputs = this.$refs.otpInputs as HTMLInputElement[];
      inputs?.[index]?.focus();
    },
    onInput(evt: Event, index: number): void {
      const target = evt.target as HTMLInputElement;
      const digit = target.value.replace(/\D/g, "").slice(-1);
      this.$set(this.digits, index, digit);
      target.value = digit;
      if (digit && index < CODE_LENGTH - 1) {
        this.$nextTick(() => this.focusInput(index + 1));
      }
    },
    onKeydown(evt: KeyboardEvent, index: number): void {
      if (evt.key === "Backspace" && !this.digits[index] && index > 0) {
        this.focusInput(index - 1);
      }
    },
    onPaste(evt: ClipboardEvent): void {
      evt.preventDefault();
      const pasted = (evt.clipboardData?.getData("text") ?? "")
        .replace(/\D/g, "")
        .slice(0, CODE_LENGTH);
      this.digits.forEach((_, index) =>
        this.$set(this.digits, index, pasted[index] ?? ""),
      );
      this.$nextTick(() =>
        this.focusInput(Math.min(pasted.length, CODE_LENGTH - 1)),
      );
    },
    clearOtpDigits(): void {
      this.digits.forEach((_, index) => this.$set(this.digits, index, ""));
      this.$nextTick(() => this.focusInput(0));
    },
    clearResendCooldown(): void {
      if (this.resendCooldownTimer !== null) {
        clearInterval(this.resendCooldownTimer);
        this.resendCooldownTimer = null;
      }
    },
    startResendCooldown(seconds: number): void {
      this.clearResendCooldown();
      if (seconds <= 0) {
        this.resendCooldownSeconds = 0;
        return;
      }
      this.resendCooldownSeconds = seconds;
      this.resendCooldownTimer = setInterval(() => {
        this.resendCooldownSeconds -= 1;
        if (this.resendCooldownSeconds <= 0) {
          this.clearResendCooldown();
        }
      }, 1000);
    },
    async leaveTwoFactor(): Promise<void> {
      await this.$store.dispatch("clearTwoFactorChallenge");
      await this.$store.dispatch("acceptWarning");
      this.resendFeedbackMessage = "";
      this.$emit("sign-in");
      await this.$router.replace("/login").catch(() => undefined);
    },
    async applyVerifyToken(token: string): Promise<void> {
      if (!token) {
        return;
      }
      const nextChallenge = {
        ...this.challenge,
        temporaryToken: token,
        phoneLast4: this.challenge.phoneLast4 || this.phoneLast4,
        initialError: "",
      };
      await this.$store.dispatch("setTwoFactorChallenge", nextChallenge);
      const nextRoute = buildTwoFactorRoute(nextChallenge);
      await this.$router.replace(nextRoute).catch(() => undefined);
    },
    async resendCode(): Promise<void> {
      if (!this.canResend) {
        return;
      }
      this.isResending = true;
      try {
        if (this.verifyToken) {
          const result = (await this.$store.dispatch(
            "twoFactorResend",
            this.verifyToken,
          )) as TwoFactorResendResult | undefined;
          if (!result) {
            return;
          }
          if (result.success) {
            this.clearOtpDigits();
            this.resendFeedbackMessage = TWO_FACTOR_RESEND_SUCCESS_MESSAGE;
            await this.applyVerifyToken(result.token);
            this.startResendCooldown(TWO_FACTOR_RESEND_COOLDOWN_SECONDS);
            return;
          }
          this.resendFeedbackMessage = "";
          this.startResendCooldown(
            resendFailureCooldownSeconds(result.waitSeconds),
          );
          return;
        }
        if (this.challenge.oathToken && this.challenge.phone) {
          const sentToken = await this.$store.dispatch("twoFactorSend", {
            token: this.challenge.oathToken,
            phone: this.challenge.phone,
          });
          if (sentToken) {
            this.clearOtpDigits();
            this.resendFeedbackMessage = TWO_FACTOR_RESEND_SUCCESS_MESSAGE;
            await this.applyVerifyToken(sentToken);
            this.startResendCooldown(TWO_FACTOR_RESEND_COOLDOWN_SECONDS);
          } else {
            this.resendFeedbackMessage = "";
            this.startResendCooldown(TWO_FACTOR_RESEND_COOLDOWN_SECONDS);
          }
          return;
        }
        this.resendFeedbackMessage = "";
        await this.$store.dispatch("setWarning", MFA_SESSION_EXPIRED_MESSAGE);
      } finally {
        this.isResending = false;
      }
    },
    async submitForm(): Promise<void> {
      if (
        !this.verifyToken ||
        this.code.length !== CODE_LENGTH ||
        this.isSubmitting
      ) {
        return;
      }
      this.resendFeedbackMessage = "";
      this.isSubmitting = true;
      try {
        const isV3Ready = await this.$store.dispatch("twoFactorSignIn", {
          router: this.$router,
          payload: {
            token: this.verifyToken,
            code: this.code,
            platform: "web",
          },
        });
        if (isV3Ready === false) {
          this.$emit("not-v3");
        }
      } finally {
        this.isSubmitting = false;
      }
    },
  },
});
